import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { lcoAPI, ticketAPI, paymentAPI, lcoStaffAPI, lcoPaymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const LCODashboard = () => {
  const { user, tenant, refreshLcoFeatures } = useAuth();
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab');
  const primary = tenant?.branding?.primary_color || '#1B6B3A';

  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState(urlTab||'dashboard');
  const [payments, setPayments] = React.useState([]);
  const [paySummary, setPaySummary] = React.useState(null);
  const [payFilter, setPayFilter] = React.useState('pending');
  const [payLoading, setPayLoading] = React.useState(false);
  const [collectModal, setCollectModal] = React.useState(null);
  const [collectAmt, setCollectAmt] = React.useState('');
  const [collectMode, setCollectMode] = React.useState('Cash');
  const [collectNote, setCollectNote] = React.useState('');
  const [collectDate, setCollectDate] = React.useState('');
  const [editEntryModal, setEditEntryModal] = React.useState(null); // {collection, entryIndex}
  const [editEntryAmt, setEditEntryAmt] = React.useState('');
  const [editEntryMode, setEditEntryMode] = React.useState('Cash');
  const [editEntryNote, setEditEntryNote] = React.useState('');
  const [editEntryDate, setEditEntryDate] = React.useState('');
  const [historyDrawer, setHistoryDrawer] = React.useState(null); // {username, name}
  const [historyData, setHistoryData] = React.useState(null);
  const [historyYear, setHistoryYear] = React.useState('');
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [paySearch, setPaySearch] = React.useState('');
  const [custSpecialFilter, setCustSpecialFilter] = React.useState(''); // expiring | new_this_month | new_last_month
  const [paySyncStatus, setPaySyncStatus] = React.useState(null);
  const [payMonthLabel, setPayMonthLabel] = React.useState('');
  const [payLastSummary, setPayLastSummary] = React.useState(null);
  const [paySetup, setPaySetup] = React.useState(null);
  const [sendingLink, setSendingLink] = React.useState({});
  const [payLastMonthLabel, setPayLastMonthLabel] = React.useState('');
  const [payFromDate, setPayFromDate] = React.useState('');
  const [payToDate, setPayToDate] = React.useState('');
  const payFromRef = React.useRef('');
  const payToRef = React.useRef('');
  const [selPayMonth, setSelPayMonth] = React.useState('');
  const [selPayYear, setSelPayYear] = React.useState('');
  const [staff, setStaff] = React.useState([]);
  const [staffLoading, setStaffLoading] = React.useState(false);
  const [showStaffForm, setShowStaffForm] = React.useState(false);
  const [editStaff, setEditStaff] = React.useState(null);
  const [staffForm, setStaffForm] = React.useState({name:'',mobile:'',role:'lco_tech',password:''});
  const [custFilter, setCustFilter] = useState('');
  const [custStatus, setCustStatus] = useState('');
  const [custPage, setCustPage] = useState(1);
  const [custTotal, setCustTotal] = useState(0);
  const [custPages, setCustPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketStatus, setTicketStatus] = useState('');
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwd, setPwd] = useState({current:'', newp:'', confirm:''});
  const [pwdMsg, setPwdMsg] = useState('');
  const [search, setSearch] = useState('');
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({customer_name:'',customer_phone:'',customer_address:'',customer_connection_id:'',category:'no_internet',description:'',priority:'medium'});
  const [ticketMsg, setTicketMsg] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [olts, setOlts] = useState([]);
  const [selectedOlt, setSelectedOlt] = useState(null);
  const [oltOnts, setOltOnts] = useState([]);
  const [oltLoading, setOltLoading] = useState(false);
  const [ontSearch, setOntSearch] = useState('');
  const [expandedPons, setExpandedPons] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showCongModal, setShowCongModal] = useState(false);
  const [congData, setCongData] = useState(null);
  const [congLoading, setCongLoading] = useState(false);
  const [congTitle, setCongTitle] = useState('');

  useEffect(() => {
    lcoAPI.profile().then(r => { setProfile(r.data); setLoading(false); if(r.data?.features && refreshLcoFeatures) refreshLcoFeatures(); }).catch(()=>setLoading(false));
    lcoPaymentAPI.getSetup().then(r=>{ console.log('PaySetup response:',r.data); if(r.data?.setup) setPaySetup(r.data.setup); }).catch(e=>console.log('PaySetup error:',e.message));
  }, []);

  useEffect(() => {
    if(urlTab) setTab(urlTab);
  }, [urlTab]);

  useEffect(() => {
    if(tab==='customers') loadCustomers();
  }, [tab, custStatus, custPage, search]);

  useEffect(() => {
    if(tab==='tickets') loadTickets();
  }, [tab, ticketStatus, ticketPage]);

  useEffect(() => {
    if(tab==='network') lcoAPI.olts().then(r=>setOlts(r.data.olts||[]));
    if(tab==='payments') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth()+1).padStart(2,'0');
      const last = new Date(y, now.getMonth()+1, 0).getDate();
      const from = y+'-'+m+'-01';
      const to = y+'-'+m+'-'+String(last).padStart(2,'0');
      setPayFromDate(from); setPayToDate(to);
      payFromRef.current = from; payToRef.current = to;
      setSelPayMonth(String(now.getMonth()+1));
      setSelPayYear(String(y));
      loadPaymentsWithDates(from, to);
    }
    if(tab==='staff') loadStaff();
  }, [tab]);

  const loadCustomers = () => {
    lcoAPI.customers({status:custStatus, search, page:custPage, limit:50, ...(custSpecialFilter?{special:custSpecialFilter}:{})})
      .then(r=>{ setCustomers(r.data.customers); setCustTotal(r.data.total); setCustPages(r.data.pages); });
  };

  const runCongestion = async (olt, e) => {
    e.stopPropagation();
    setCongLoading(true);
    setShowCongModal(true);
    setCongData(null);
    setCongTitle(olt.switch_name||olt.switch_ip);
    try {
      const [histR, oltR] = await Promise.all([
        lcoAPI.traffic(olt.switch_ip),
        lcoAPI.oltOnts(olt._id)
      ]);
      const history = histR.data.history||[];
      const oltData = oltR.data.olt;
      const ponPorts = oltData?.pon_ports||[];

      if(history.length < 2) {
        setCongData({noData:true});
        setCongLoading(false);
        return;
      }

      const GPON_DOWN = 2500, GPON_UP = 1250;
      const lastH = history[history.length-1];
      const portStats = [];

      (lastH.interfaces||[]).forEach(iface=>{
        if(!iface.name||!iface.name.startsWith('PON')) return;
        const outMbps = Math.round(iface.out_bps/1e6*10)/10;
        const inMbps = Math.round(iface.in_bps/1e6*10)/10;
        const downUtil = Math.round(outMbps/GPON_DOWN*100);
        const upUtil = Math.round(inMbps/GPON_UP*100);
        const maxUtil = Math.max(downUtil,upUtil);

        let peakDown=0,peakUp=0,sumDown=0,sumUp=0;
        history.forEach(h=>{
          const i2=(h.interfaces||[]).find(i=>i.name===iface.name);
          if(!i2) return;
          const d=i2.out_bps/1e6, u=i2.in_bps/1e6;
          if(d>peakDown) peakDown=d;
          if(u>peakUp) peakUp=u;
          sumDown+=d; sumUp+=u;
        });
        const avgDown=Math.round(sumDown/history.length*10)/10;
        const avgUp=Math.round(sumUp/history.length*10)/10;

        const ponPort = ponPorts.find(p=>p.pon==iface.name.replace('PON',''));
        const ontsTotal = iface.onts_total||ponPort?.total||0;
        const ontsOnline = iface.onts_online||ponPort?.online||0;
        const perOntDown = ontsOnline>0?Math.round(outMbps/ontsOnline*100)/100:0;

        let status='healthy',color='#10b981',icon='✅';
        if(maxUtil>=90){status='congested';color='#ef4444';icon='🔴';}
        else if(maxUtil>=70){status='high load';color='#f59e0b';icon='🟡';}
        else if(maxUtil>=50){status='moderate';color='#3b82f6';icon='🔵';}

        portStats.push({name:iface.name,outMbps,inMbps,downUtil,upUtil,maxUtil,avgDown,avgUp,peakDown:Math.round(peakDown*10)/10,peakUp:Math.round(peakUp*10)/10,ontsTotal,ontsOnline,perOntDown,status,color,icon});
      });

      portStats.sort((a,b)=>b.maxUtil-a.maxUtil);
      setCongData({portStats, history: history.length});
    } catch(err) { setCongData({error:err.message}); }
    setCongLoading(false);
  };

  const runOltAI = async (olt, e) => {
    e.stopPropagation();
    setAiLoading(true);
    setShowAiModal(true);
    setAiAnalysis(null);
    try {
      const r = await lcoAPI.oltOnts(olt._id);
      const oltData = r.data.olt;
      const onts = oltData?.onts||[];
      const ponPorts = oltData?.pon_ports||[];
      const totalOnts = oltData?.total_onts||onts.length;
      const onlineOnts = oltData?.onts_online||onts.filter(o=>o.status==='online').length;
      const offlineOnts = totalOnts - onlineOnts;
      const health = totalOnts>0?Math.round(onlineOnts/totalOnts*100):0;
      
      const alerts = [];
      // Check PON ports
      ponPorts.forEach(p=>{
        const pct = p.total>0?Math.round(p.online/p.total*100):0;
        if(pct===0&&p.total>0) alerts.push({sev:'critical',msg:'PON'+p.pon+': ALL '+p.total+' ONTs OFFLINE',rec:'Complete PON failure - check OLT PON port, fiber trunk and main splitter for PON'+p.pon});
        else if(pct<50&&p.total>2) alerts.push({sev:'critical',msg:'PON'+p.pon+': Only '+p.online+'/'+p.total+' online ('+pct+'%)',rec:'Possible fiber break or splitter fault on PON'+p.pon});
        else if(pct<80&&p.total>2) alerts.push({sev:'warning',msg:'PON'+p.pon+': '+p.online+'/'+p.total+' online ('+pct+'%)',rec:'Multiple offline ONTs - check for localized power issues'});
      });
      // Critical RX
      const critRx = onts.filter(o=>o.rx_power!=null&&o.rx_power<=-27&&o.status==='online');
      const margRx = onts.filter(o=>o.rx_power!=null&&o.rx_power>-27&&o.rx_power<=-25&&o.status==='online');
      if(critRx.length>0) alerts.push({sev:'critical',msg:'🔴 '+critRx.length+' ONTs with critical RX signal (≤-27dBm)',rec:critRx.map(o=>o.name+' ('+o.serial+') RX:'+o.rx_power+'dBm').join('\n')});
      if(margRx.length>0) alerts.push({sev:'warning',msg:'🟡 '+margRx.length+' ONTs with marginal RX signal (-25 to -27dBm)',rec:margRx.map(o=>o.name+' ('+o.serial+') RX:'+o.rx_power+'dBm').join('\n')});
      
      setAiAnalysis({oltName:olt.switch_name||olt.switch_ip, totalOnts, onlineOnts, offlineOnts, health, alerts});
    } catch(err) { setAiAnalysis({error:err.message}); }
    setAiLoading(false);
  };

  const loadOltOnts = async (olt) => {
    setSelectedOlt(olt);
    setOltLoading(true);
    setOntSearch('');
    try {
      const r = await lcoAPI.oltOnts(olt._id);
      setOltOnts(r.data.olt?.onts||[]);
    } catch(e) { setOltOnts([]); }
    setOltLoading(false);
  };

  const searchCustomers = async (q) => {
    setCustSearch(q);
    if(q.length < 2) { setCustResults([]); return; }
    const r = await lcoAPI.customers({search:q, limit:8});
    setCustResults(r.data.customers||[]);
  };

  const selectCustomer = (c) => {
    setTicketForm(f=>({...f, customer_name:c.name, customer_phone:c.mobile, customer_address:c.address, customer_connection_id:c.username}));
    setCustSearch(c.name+' ('+c.username+')');
    setCustResults([]);
  };

  const submitTicket = async () => {
    if(!ticketForm.description) { setTicketMsg('Please enter description'); return; }
    setTicketLoading(true);
    try {
      await lcoAPI.createTicket(ticketForm);
      setTicketMsg('✅ Ticket created successfully!');
      setTicketForm({customer_name:'',customer_phone:'',customer_address:'',customer_connection_id:'',category:'no_internet',description:'',priority:'medium'});
      setCustSearch('');
      setTimeout(()=>{ setShowTicketForm(false); setTicketMsg(''); loadTickets(); }, 2000);
    } catch(e) {
      setTicketMsg('❌ '+(e.response?.data?.message||'Error creating ticket'));
    }
    setTicketLoading(false);
  };

  const loadTickets = () => {
    lcoAPI.tickets({status:ticketStatus, page:ticketPage, limit:20})
      .then(r=>setTickets(r.data.tickets));
  };

  const changePwd = async () => {
    if(pwd.newp !== pwd.confirm) { setPwdMsg('Passwords do not match'); return; }
    try {
      await lcoAPI.changePassword({current_password:pwd.current, new_password:pwd.newp});
      setPwdMsg('Password changed successfully!');
      setPwd({current:'',newp:'',confirm:''});
      setTimeout(()=>{ setShowPwdForm(false); setPwdMsg(''); }, 2000);
    } catch(e) { setPwdMsg(e.response?.data?.message||'Error'); }
  };

  const stats = profile?.stats;
  const lco = profile?.lco;
  const isPilotLco = profile?.features?.collections || false;
  const hasExpenses = profile?.features?.expenses || false;
  const hasInventory = profile?.features?.inventory || false;
  const hasStaff = profile?.features?.staff || false;
  // Show expiring/new stats to all LCOs regardless of pilot status
  const showLcoStats = !!profile;

  // Reset special filter when normal nav happens
  const navToCustomers = (status) => { setCustStatus(status); setCustSpecialFilter(''); setCustPage(1); setTab('customers'); };

  const StatCard = ({label, value, color, bg, onClick}) => (
    <div onClick={onClick} style={{background:'white',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',border:'1px solid #f1f5f9',cursor:onClick?'pointer':'default',transition:'all 0.2s'}}
      onMouseEnter={e=>{if(onClick)e.currentTarget.style.transform='translateY(-2px)'}}
      onMouseLeave={e=>{if(onClick)e.currentTarget.style.transform='translateY(0)'}}>
      <p style={{margin:'0 0 6px',fontSize:'11px',color:'#64748b',textTransform:'uppercase',letterSpacing:'1px',fontWeight:'600'}}>{label}</p>
      <h2 style={{margin:0,fontSize:'32px',fontWeight:'800',color}}>{value??'—'}</h2>
    </div>
  );

  const loadStaff = async () => {
    setStaffLoading(true);
    try {
      const r = await lcoStaffAPI.getAll();
      setStaff(r.data.data||[]);
    } catch(e){ console.error(e); }
    setStaffLoading(false);
  };

  const submitStaff = async () => {
    if(!staffForm.name||!staffForm.mobile||!staffForm.role) return alert('Name, mobile and role required');
    if(!editStaff && !staffForm.password) return alert('Password required');
    try {
      if(editStaff) await lcoStaffAPI.update(editStaff._id, staffForm);
      else await lcoStaffAPI.create(staffForm);
      setShowStaffForm(false);
      setEditStaff(null);
      setStaffForm({name:'',email:'',phone:'',role:'lco_tech',password:''});
      loadStaff();
    } catch(e){ alert('Error: '+(e.response?.data?.message||e.message)); }
  };

  const deleteStaff = async (id) => {
    if(!window.confirm('Delete this staff member?')) return;
    await lcoStaffAPI.remove(id);
    loadStaff();
  };

  const PAY_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const getPayYears = () => { const y=new Date().getFullYear(); const arr=[]; for(let i=2020;i<=y+2;i++) arr.push(i); return arr; };
  const monthYearRange = (month,year) => {
    const last = new Date(year,month,0).getDate();
    const m = String(month).padStart(2,'0');
    return {from:year+'-'+m+'-01', to:year+'-'+m+'-'+String(last).padStart(2,'0')};
  };

  const loadHistory = async (username, year) => {
    setHistoryLoading(true);
    try {
      const r = await paymentAPI.history(username, year||'');
      setHistoryData(r.data);
      if(!year && r.data.years?.length>0) setHistoryYear(r.data.years[0].toString());
    } catch(e){ console.error(e); }
    setHistoryLoading(false);
  };

  const openHistory = (p) => {
    setHistoryDrawer({username:p.username, name:p.customer_name});
    setHistoryData(null);
    setHistoryYear('');
    loadHistory(p.username, new Date().getFullYear().toString());
  };

  const loadPaymentsWithDates = async (from, to, filter) => {
    const activeFilter = filter !== undefined ? filter : payFilter;
    setPayLoading(true);
    try {
      const [pr, sr, syncR] = await Promise.all([
        paymentAPI.all({...(activeFilter!=='all'?{status:activeFilter}:{}), ...(from?{from_date:from}:{}), ...(to?{to_date:to}:{})}),
        paymentAPI.summary(),
        paymentAPI.syncStatus()
      ]);
      setPayments(pr.data.data||[]);
      setPaySummary(sr.data.summary||null);
      setPayMonthLabel(sr.data.month_label||'');
      setPayLastSummary(sr.data.last_summary||null);
      setPayLastMonthLabel(sr.data.last_month_label||'');
      setPaySyncStatus(syncR.data||null);
    } catch(e){ console.error(e); }
    setPayLoading(false);
  };

  const loadPayments = async () => {
    setPayLoading(true);
    try {
      const [pr, sr, syncR] = await Promise.all([
        paymentAPI.all({...(payFilter!=='all'?{status:payFilter}:{}), ...(payFromDate?{from_date:payFromDate}:{}), ...(payToDate?{to_date:payToDate}:{})}),
        paymentAPI.summary(),
        paymentAPI.syncStatus()
      ]);
      setPayments(pr.data.data||[]);
      setPaySummary(sr.data.summary||null);
      setPayMonthLabel(sr.data.month_label||'');
      setPayLastSummary(sr.data.last_summary||null);
      setPayLastMonthLabel(sr.data.last_month_label||'');
      setPaySyncStatus(syncR.data||null);
    } catch(e){ console.error(e); }
    setPayLoading(false);
  };

  const submitEditEntry = async () => {
    if(!editEntryModal) return;
    try {
      await paymentAPI.editEntry(editEntryModal.collection._id, editEntryModal.entryIndex, {
        amount: parseFloat(editEntryAmt),
        mode: editEntryMode,
        note: editEntryNote,
        date: editEntryDate
      });
      setEditEntryModal(null);
      loadPaymentsWithDates(payFromRef.current, payToRef.current);
    } catch(e){ alert('Error: '+e.message); }
  };

  const submitCollection = async () => {
    if(!collectModal || !collectAmt) return;
    try {
      await paymentAPI.collect({
        collection_id: collectModal._id,
        amount: parseFloat(collectAmt),
        mode: collectMode,
        note: collectNote,
        date: collectDate
      });
      setCollectModal(null);
      setCollectAmt(''); setCollectNote(''); setCollectDate('');
      loadPaymentsWithDates(payFromRef.current, payToRef.current);
    } catch(e){ alert('Error: '+e.message); }
  };

  const statusColor = {Active:'#10b981',Inactive:'#94a3b8',Suspended:'#ef4444',Dormant:'#f59e0b'};
  const statusBg = {Active:'#ecfdf5',Inactive:'#f8fafc',Suspended:'#fef2f2',Dormant:'#fffbeb'};
  const ticketStatusColor = {open:'#3b82f6',assigned:'#8b5cf6',in_progress:'#f59e0b',escalated:'#ef4444',resolved:'#10b981',closed:'#6b7280'};

  if(loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px'}}><div style={{width:'40px',height:'40px',border:`3px solid ${primary}22`,borderTop:`3px solid ${primary}`,borderRadius:'50%',animation:'spin 1s linear infinite'}}/></div>;

  return (
    <div style={{padding:'24px',maxWidth:'1200px',margin:'0 auto'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'24px'}}>
        <div>
          <h1 style={{margin:'0 0 4px',fontSize:'24px',fontWeight:'800',color:'#0f172a'}}>{lco?.name}</h1>
          <p style={{margin:0,fontSize:'13px',color:'#64748b'}}>LCO Code: <strong>{lco?.code}</strong> · {lco?.subdistribution_id?.name} · Tech: {lco?.assigned_technician?.name||'Not assigned'}</p>
        </div>
        <button onClick={()=>setShowPwdForm(!showPwdForm)} style={{padding:'8px 16px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>🔐 Change Password</button>
      </div>

      {/* Change Password Form */}
      {showPwdForm && (
        <div style={{background:'white',borderRadius:'12px',padding:'20px',marginBottom:'20px',border:'1px solid #e2e8f0',maxWidth:'400px'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'16px',fontWeight:'700'}}>Change Password</h3>
          {['current','newp','confirm'].map((f,i)=>(<div key={f} style={{marginBottom:'12px'}}>
            <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'4px'}}>{['Current Password','New Password','Confirm New Password'][i]}</div>
            <input type="password" style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}
              value={pwd[f]} onChange={e=>setPwd(p=>({...p,[f]:e.target.value}))} />
          </div>))}
          {pwdMsg && <p style={{color:pwdMsg.includes('success')?'#10b981':'#ef4444',fontSize:'13px',margin:'8px 0'}}>{pwdMsg}</p>}
          <button onClick={changePwd} style={{padding:'8px 20px',background:primary,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>Update Password</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:'4px',marginBottom:'24px',background:'#f8fafc',padding:'4px',borderRadius:'10px',width:'fit-content'}}>
        {[['dashboard','🏠 Dashboard'],['customers','👥 My Customers'],...(isPilotLco?[['payments','💰 Collections']]:[]),['tickets','🎫 My Tickets'],['create','➕ New Ticket'],['network','📡 My Network'],...(hasStaff&&user?.role==='lco'?[['staff','👥 My Staff']]:[])].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'8px 20px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',background:tab===t?'white':'transparent',color:tab===t?primary:'#64748b',boxShadow:tab===t?'0 1px 3px rgba(0,0,0,0.1)':'none',transition:'all 0.2s'}}>{l}</button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {tab==='dashboard' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'16px',marginBottom:'24px'}}>
            <StatCard label="Total Customers" value={stats?.total} color={primary} onClick={()=>navToCustomers('')} />
            <StatCard label="Active" value={stats?.active} color="#10b981" onClick={()=>navToCustomers('Active')} />
            <StatCard label="Inactive" value={stats?.inactive} color="#94a3b8" onClick={()=>navToCustomers('Inactive')} />
            <StatCard label="Suspended" value={stats?.suspended} color="#ef4444" onClick={()=>navToCustomers('Suspended')} />

            <StatCard label="Pending Activation" value={stats?.pending} color="#7c3aed" onClick={()=>navToCustomers('New - Activation Pending')} />
            <StatCard label="Open Tickets" value={stats?.openTickets} color="#3b82f6" onClick={()=>setTab('tickets')} />
            <StatCard label="Resolved This Month" value={stats?.resolvedThisMonth} color="#8b5cf6" />
            {showLcoStats && <>
              <StatCard label="⏰ Expiring in 7 Days" value={stats?.expiringCount} color="#f59e0b" bg="#fffbeb"
                onClick={()=>{setTab('customers');setCustStatus('Active');setCustSpecialFilter('expiring');setCustPage(1);}} />
              <StatCard label={`🆕 New — ${new Date().toLocaleString('en-IN',{month:'short'})}`}
                value={stats?.newThisMonth}
                color="#06b6d4" bg="#ecfeff"
                onClick={()=>{setTab('customers');setCustSpecialFilter('new_this_month');setCustPage(1);}} />
              <StatCard label={`🆕 New — ${new Date(new Date().getFullYear(),new Date().getMonth()-1,1).toLocaleString('en-IN',{month:'short'})}`}
                value={stats?.newLastMonth}
                color="#0891b2" bg="#f0f9ff"
                onClick={()=>{setTab('customers');setCustSpecialFilter('new_last_month');setCustPage(1);}} />
              <StatCard label="⚠️ Overdue 30+ Days" value={stats?.overdue30} color="#dc2626" bg="#fef2f2"
                onClick={()=>{setTab('payments');setPayFilter('pending');}} />
            </>}
          </div>
          <div style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #f1f5f9'}}>
            <h3 style={{margin:'0 0 12px',fontSize:'15px',fontWeight:'700'}}>LCO Details</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',fontSize:'13px'}}>
              <div><span style={{color:'#64748b'}}>Name:</span> <strong>{lco?.name}</strong></div>
              <div><span style={{color:'#64748b'}}>Code:</span> <strong>{lco?.code}</strong></div>
              <div><span style={{color:'#64748b'}}>Sub-District:</span> <strong>{lco?.subdistribution_id?.name}</strong></div>
              <div><span style={{color:'#64748b'}}>Technician:</span> <strong>{lco?.assigned_technician?.name||'Not assigned'}</strong></div>
              <div><span style={{color:'#64748b'}}>Tech Phone:</span> <strong>{lco?.assigned_technician?.phone||'-'}</strong></div>
              <div><span style={{color:'#64748b'}}>Contact:</span> <strong>{lco?.contact?.person_name}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {tab==='customers' && (
        <div>
          <div style={{display:'flex',gap:'12px',marginBottom:'16px',flexWrap:'wrap'}}>
            <input value={search} onChange={e=>{setSearch(e.target.value);setCustPage(1);}} placeholder="Search by name, username, phone..." style={{flex:1,minWidth:'200px',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px'}} />
            <select value={custStatus} onChange={e=>{setCustStatus(e.target.value);setCustPage(1);}} style={{padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px'}}>
              <option value="">All Status</option>
              {['Active','Inactive','Suspended','Dormant'].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
            <div style={{padding:'12px 16px',background:'#f8fafc',fontSize:'12px',color:'#64748b',fontWeight:'600'}}>
              Showing {customers.length} of {custTotal} customers
            </div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'#f8fafc'}}>
                {['Username','Name','Mobile','Plan','Status'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'12px',color:'#64748b',fontWeight:'600'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {customers.map((c,i)=>(
                  <tr key={c.username} onClick={()=>setSelectedCustomer(c)} style={{borderTop:'1px solid #f1f5f9',cursor:'pointer',background:i%2?'#f8fafc':'white'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=i%2?'#f8fafc':'white'}>
                    <td style={{padding:'10px 14px',fontFamily:'monospace',fontSize:'13px',fontWeight:'600'}}>{c.username}</td>
                    <td style={{padding:'10px 14px',fontSize:'13px'}}>{c.name}</td>
                    <td style={{padding:'10px 14px',fontSize:'13px'}}>{c.mobile}</td>
                    <td style={{padding:'10px 14px',fontSize:'12px',color:'#64748b'}}>{c.plan}</td>
                    <td style={{padding:'10px 14px'}}><span style={{padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',background:statusBg[c.status]||'#f8fafc',color:statusColor[c.status]||'#64748b'}}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {custPages>1 && <div style={{padding:'12px 16px',display:'flex',gap:'8px',justifyContent:'center'}}>
              {Array.from({length:custPages},(_, i)=>(
                <button key={i} onClick={()=>setCustPage(i+1)} style={{padding:'6px 12px',border:'1px solid #e2e8f0',borderRadius:'6px',cursor:'pointer',background:custPage===i+1?primary:'white',color:custPage===i+1?'white':'#64748b',fontWeight:'600',fontSize:'12px'}}>{i+1}</button>
              ))}
            </div>}
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {tab==='tickets' && (
        <div>
          <div style={{display:'flex',gap:'12px',marginBottom:'16px',justifyContent:'space-between',alignItems:'center'}}>
            <select value={ticketStatus} onChange={e=>setTicketStatus(e.target.value)} style={{padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px'}}>
              <option value="">All Status</option>
              {['open','assigned','in_progress','escalated','resolved','closed'].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'#f8fafc'}}>
                {['Ticket #','Customer','Complaint','Status','Assigned To','Date'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'12px',color:'#64748b',fontWeight:'600'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {tickets.length===0 && <tr><td colSpan={6} style={{padding:'32px',textAlign:'center',color:'#94a3b8'}}>No tickets found</td></tr>}
                {tickets.map((t,i)=>(
                  <tr key={t._id} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#f8fafc':'white'}}>
                    <td style={{padding:'10px 14px',fontFamily:'monospace',fontSize:'12px',fontWeight:'600'}}>#{t.ticket_number||t._id.toString().slice(-6)}</td>
                    <td style={{padding:'10px 14px',fontSize:'13px'}}><div>{t.customer?.name||'-'}</div><div style={{fontSize:'11px',color:'#94a3b8'}}>{t.customer?.phone}</div></td>
                    <td style={{padding:'10px 14px',fontSize:'13px'}}>{t.complaint?.type||'-'}</td>
                    <td style={{padding:'10px 14px'}}><span style={{padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',background:ticketStatusColor[t.status]+'22',color:ticketStatusColor[t.status]}}>{t.status}</span></td>
                    <td style={{padding:'10px 14px',fontSize:'13px'}}>{t.assigned_to?.name||'Unassigned'}</td>
                    <td style={{padding:'10px 14px',fontSize:'12px',color:'#64748b'}}>{new Date(t.createdAt).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Ticket Tab */}
      {tab==='create' && (
        <div style={{background:'white',borderRadius:'16px',padding:'28px',border:'1px solid #f1f5f9',maxWidth:'600px'}}>
          <h3 style={{margin:'0 0 20px',fontSize:'18px',fontWeight:'800'}}>➕ Create New Ticket</h3>
          
          {/* Customer Search */}
          <div style={{marginBottom:'16px',position:'relative'}}>
            <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'6px'}}>SEARCH CUSTOMER (BMS)</div>
            <input value={custSearch} onChange={e=>searchCustomers(e.target.value)}
              placeholder="Type username, name or phone..." 
              style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}} />
            {custResults.length>0 && (
              <div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid #e2e8f0',borderRadius:'8px',boxShadow:'0 4px 12px rgba(0,0,0,0.1)',zIndex:100,maxHeight:'200px',overflowY:'auto'}}>
                {custResults.map(c=>(
                  <div key={c.username} onClick={()=>selectCustomer(c)} style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid #f1f5f9',fontSize:'13px'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                    onMouseLeave={e=>e.currentTarget.style.background='white'}>
                    <strong>{c.name}</strong> <span style={{color:'#64748b'}}>({c.username})</span> · {c.mobile}
                    <span style={{marginLeft:'8px',padding:'2px 8px',borderRadius:'20px',fontSize:'11px',background:c.status==='Active'?'#ecfdf5':'#fef2f2',color:c.status==='Active'?'#10b981':'#ef4444'}}>{c.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auto-filled customer details */}
          {ticketForm.customer_connection_id && (
            <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'8px',padding:'12px',marginBottom:'16px',fontSize:'13px'}}>
              <div style={{fontWeight:'700',color:'#15803d',marginBottom:'6px'}}>✅ Customer Selected</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',color:'#475569'}}>
                <div>👤 {ticketForm.customer_name}</div>
                <div>📱 {ticketForm.customer_phone}</div>
                <div>🔗 {ticketForm.customer_connection_id}</div>
                <div>📍 {ticketForm.customer_address?.substring(0,30)}...</div>
              </div>
            </div>
          )}

          {/* Complaint Type */}
          <div style={{marginBottom:'14px'}}>
            <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'6px'}}>COMPLAINT TYPE *</div>
            <select value={ticketForm.category} onChange={e=>setTicketForm(f=>({...f,category:e.target.value}))}
              style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}>
              <option value="no_internet">No Internet</option>
              <option value="slow_speed">Slow Speed</option>
              <option value="intermittent">Intermittent Connection</option>
              <option value="tv_issue">TV Issue</option>
              <option value="router_issue">Router/Device Issue</option>
              <option value="new_connection">New Connection Request</option>
              <option value="billing">Billing Issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Priority */}
          <div style={{marginBottom:'14px'}}>
            <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'6px'}}>PRIORITY</div>
            <select value={ticketForm.priority} onChange={e=>setTicketForm(f=>({...f,priority:e.target.value}))}
              style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          {/* Description */}
          <div style={{marginBottom:'20px'}}>
            <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'6px'}}>DESCRIPTION *</div>
            <textarea value={ticketForm.description} onChange={e=>setTicketForm(f=>({...f,description:e.target.value}))}
              placeholder="Describe the issue in detail..."
              rows={4} style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',resize:'vertical',boxSizing:'border-box'}} />
          </div>

          {ticketMsg && <p style={{color:ticketMsg.includes('✅')?'#10b981':'#ef4444',fontSize:'13px',margin:'0 0 16px'}}>{ticketMsg}</p>}
          
          <button onClick={submitTicket} disabled={ticketLoading}
            style={{padding:'12px 28px',background:primary,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'700',fontSize:'14px',opacity:ticketLoading?0.7:1}}>
            {ticketLoading?'Creating...':'🎫 Create Ticket'}
          </button>
        </div>
      )}

      {/* Network Tab */}
      {tab==='payments' && (
        <div>
          {/* Sync Status */}
          {paySyncStatus?.last_sync && (
            <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'8px',padding:'8px 14px',marginBottom:'12px',fontSize:'12px',color:'#16a34a',display:'flex',alignItems:'center',gap:'8px'}}>
              ✅ <strong>Last Updated:</strong> {new Date(paySyncStatus.last_sync).toLocaleString('en-IN')}
            </div>
          )}

          {/* Summary Cards */}
          {paySummary && (<>
            <div style={{marginBottom:'4px',fontSize:'12px',fontWeight:'700',color:'#64748b'}}>📅 Summary: {payMonthLabel} (Current Month)</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px',marginBottom:'16px'}}>
              {[
                {l:'Total Renewals',v:paySummary.total_renewals,c:'#2563eb',f:'all'},
                {l:'Online Paid',v:paySummary.online,c:'#10b981',f:'online'},
                {l:'Cash Total',v:paySummary.cash_total,c:'#f59e0b',f:'all'},
                {l:'Collected',v:paySummary.cash_collected,c:'#10b981',f:'collected'},
                {l:'Partial',v:paySummary.cash_partial,c:'#f59e0b',f:'partial'},
                {l:'Pending',v:paySummary.cash_pending,c:'#ef4444',f:'pending'},
                {l:'UPI/Bank Collected',v:paySummary.upi_bank||0,c:'#7c3aed',f:'upi_bank'},
                {l:'YouPI Gateway',v:paySummary.youpi||0,c:'#0891b2',f:'youpi'},
                {l:'Balance Due',v:'₹'+parseFloat(paySummary.total_balance||0).toLocaleString('en-IN'),c:'#dc2626',f:'pending'},
              ].map(s=>{
                const now=new Date();
                const y=now.getFullYear();
                const m=String(now.getMonth()+1).padStart(2,'0');
                const last=new Date(y,now.getMonth()+1,0).getDate();
                const from=y+'-'+m+'-01';
                const to=y+'-'+m+'-'+String(last).padStart(2,'0');
                return (
                <div key={s.l} onClick={()=>{
                  setPayFilter(s.f);
                  setPayFromDate(from);setPayToDate(to);
                  payFromRef.current=from;payToRef.current=to;
                  setSelPayMonth(String(now.getMonth()+1));
                  setSelPayYear(String(y));
                  loadPaymentsWithDates(from,to,s.f);
                }} style={{background:'white',borderRadius:'10px',padding:'12px',border:'2px solid #f1f5f9',
                  textAlign:'center',cursor:'pointer',transition:'all 0.15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=s.c;e.currentTarget.style.transform='scale(1.03)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#f1f5f9';e.currentTarget.style.transform='scale(1)';}}>
                  <div style={{fontSize:'18px',fontWeight:'800',color:s.c}}>{s.v}</div>
                  <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>{s.l}</div>
                  <div style={{fontSize:'9px',color:'#cbd5e1',marginTop:'2px'}}>tap to view ▶</div>
                </div>
                );
              })}
            </div>

            {/* Last Month Summary */}
            {payLastSummary && (
              <div style={{marginTop:'12px'}}>
                <div style={{marginBottom:'4px',fontSize:'12px',fontWeight:'700',color:'#94a3b8'}}>📅 Last Month: {payLastMonthLabel}</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'8px'}}>
                  {[
                    {l:'Total Renewals',v:payLastSummary.total_renewals,c:'#94a3b8'},
                    {l:'Online Paid',v:payLastSummary.online,c:'#6ee7b7'},
                    {l:'Cash Total',v:payLastSummary.cash_total,c:'#fcd34d'},
                    {l:'Collected',v:payLastSummary.cash_collected,c:'#6ee7b7'},
                    {l:'UPI/Bank',v:payLastSummary.upi_bank||0,c:'#a78bfa'},
                    {l:'Pending',v:payLastSummary.cash_pending,c:'#fca5a5'},
                    {l:'Balance Due',v:'₹'+parseFloat(payLastSummary.total_balance||0).toLocaleString('en-IN'),c:'#f87171'},
                  ].map(s=>(
                    <div key={s.l} style={{background:'#f8fafc',borderRadius:'10px',padding:'10px',border:'1px solid #f1f5f9',textAlign:'center'}}>
                      <div style={{fontSize:'16px',fontWeight:'800',color:s.c}}>{s.v}</div>
                      <div style={{fontSize:'10px',color:'#94a3b8',marginTop:'2px'}}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>)}

          {/* Month + Year filter */}
          <div style={{display:'flex',gap:'8px',marginBottom:'8px',flexWrap:'wrap',alignItems:'center'}}>
            <select value={selPayMonth} onChange={e=>{
              setSelPayMonth(e.target.value);
              if(e.target.value&&selPayYear){const {from,to}=monthYearRange(parseInt(e.target.value),parseInt(selPayYear));setPayFromDate(from);setPayToDate(to);payFromRef.current=from;payToRef.current=to;loadPaymentsWithDates(from,to);}
              else{setPayFromDate('');setPayToDate('');}
            }} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
              <option value="">📅 Month</option>
              {PAY_MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={selPayYear} onChange={e=>{
              setSelPayYear(e.target.value);
              if(selPayMonth&&e.target.value){const {from,to}=monthYearRange(parseInt(selPayMonth),parseInt(e.target.value));setPayFromDate(from);setPayToDate(to);payFromRef.current=from;payToRef.current=to;loadPaymentsWithDates(from,to);}
              else if(e.target.value){setPayFromDate(e.target.value+'-01-01');setPayToDate(e.target.value+'-12-31');payFromRef.current=e.target.value+'-01-01';payToRef.current=e.target.value+'-12-31';loadPaymentsWithDates(e.target.value+'-01-01',e.target.value+'-12-31');}
              else{setPayFromDate('');setPayToDate('');}
            }} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
              <option value="">Year</option>
              {getPayYears().map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            <input type="date" onChange={e=>{
              if(e.target.value){setPayFromDate(e.target.value);setPayToDate(e.target.value);payFromRef.current=e.target.value;payToRef.current=e.target.value;setSelPayMonth('');setSelPayYear('');}
              else{setPayFromDate('');setPayToDate('');payFromRef.current='';payToRef.current='';}
            }} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}
              title="Filter by specific date"/>
            <button onClick={()=>loadPaymentsWithDates(payFromRef.current, payToRef.current)}
              style={{padding:'6px 14px',background:primary,color:'white',border:'none',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
              🔍 Generate
            </button>
            <button onClick={()=>{setSelPayMonth('');setSelPayYear('');setPayFromDate('');setPayToDate('');loadPaymentsWithDates(payFromRef.current, payToRef.current);}}
              style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px',cursor:'pointer',background:'white',color:'#64748b'}}>
              ✕ All Time
            </button>
          </div>

          {/* Filter + Load */}
          <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap'}}>
            {[['pending','⏳ Pending'],['partial','🔶 Partial'],['collected','✅ Collected'],['online','🌐 Online'],['upi_bank','🏦 UPI/Bank'],['youpi','🔗 YouPI'],['all','📋 All']].map(([v,l])=>(
              <button key={v} onClick={()=>{setPayFilter(v);loadPaymentsWithDates(payFromRef.current,payToRef.current,v);}}
                style={{padding:'6px 14px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                  background:payFilter===v?primary:'#f1f5f9',color:payFilter===v?'white':'#64748b'}}>
                {l}
              </button>
            ))}
            <button onClick={()=>loadPaymentsWithDates(payFromRef.current, payToRef.current)} style={{padding:'6px 14px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',background:'#e2e8f0',color:'#475569',marginLeft:'auto'}}>
              🔄 Refresh
            </button>
            <button onClick={()=>{
              const filtered=payments.filter(p=>!paySearch||p.customer_name?.toLowerCase().includes(paySearch.toLowerCase())||p.username?.toLowerCase().includes(paySearch.toLowerCase()));
              const rows=[['Customer','Username','Plan','Amount','Paid','Balance','Type','Status','Date','UTR (YouPI)']];
              filtered.forEach(p=>rows.push([p.customer_name,p.username,p.plan,p.plan_amount,p.total_paid||0,p.balance||0,p.payment_type,p.collection_status,p.transaction_date?new Date(p.transaction_date).toLocaleDateString('en-IN'):'',p.youpi_utr||'',p.payment_entries?.find(e=>e.youpi_utr)?.youpi_utr||'']));
              const totAmt=filtered.reduce((a,p)=>a+(p.plan_amount||0),0);
              const totPaid=filtered.reduce((a,p)=>a+(p.total_paid||0),0);
              const totBal=filtered.reduce((a,p)=>a+(p.balance||0),0);
              rows.push(['','','TOTAL',totAmt.toFixed(2),totPaid.toFixed(2),totBal.toFixed(2),'','','']);
              const csv=rows.map(r=>r.join(',')).join('\n');
              const a=document.createElement('a');
              a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
              a.download='collections_'+payFilter+'_'+new Date().toISOString().split('T')[0]+'.csv';
              a.click();
            }} style={{padding:'6px 14px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',background:'#16a34a',color:'white'}}>
              📥 Excel
            </button>
            <button onClick={()=>{
              const filtered2=payments.filter(p=>!paySearch||p.customer_name?.toLowerCase().includes(paySearch.toLowerCase())||p.username?.toLowerCase().includes(paySearch.toLowerCase()));
              const totAmt2=filtered2.reduce((a,p)=>a+(p.plan_amount||0),0);
              const totPaid2=filtered2.reduce((a,p)=>a+(p.total_paid||0),0);
              const totBal2=filtered2.reduce((a,p)=>a+(p.balance||0),0);
              const rows=filtered2.map((p,i)=>`<tr style="background:${i%2?'#f9fafb':'white'}"><td>${p.customer_name}</td><td>${p.username}</td><td>${p.plan}</td><td style="text-align:right">₹${p.plan_amount}</td><td style="text-align:right">₹${p.total_paid||0}</td><td style="text-align:right;color:${p.balance>0?'red':'green'}">₹${p.balance||0}</td><td>${p.payment_type}</td><td>${p.collection_status}</td><td>${p.transaction_date?new Date(p.transaction_date).toLocaleDateString('en-IN'):''}</td></tr>`).join('');
              const totalRow=`<tr style="background:#1B6B3A;color:white;font-weight:bold"><td colspan="3">TOTAL (${filtered2.length} records)</td><td style="text-align:right">₹${totAmt2.toLocaleString('en-IN')}</td><td style="text-align:right">₹${totPaid2.toLocaleString('en-IN')}</td><td style="text-align:right">₹${totBal2.toLocaleString('en-IN')}</td><td colspan="3"></td></tr>`;
              const w=window.open('','_blank');
              w.document.write(`<!DOCTYPE html><html><head><title>Collections - ${payFilter}</title><style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#1B6B3A;color:white;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #eee}h2{color:#1B6B3A}.summary{display:flex;gap:20px;margin-bottom:16px}.scard{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px;text-align:center;min-width:120px}.scard .val{font-size:18px;font-weight:800;color:#16a34a}.scard .lbl{font-size:11px;color:#64748b}@media print{body{print-color-adjust:exact}}</style></head><body>
              <h2>Collections Report — ${payFilter.toUpperCase()}</h2>
              <p>Generated: ${new Date().toLocaleString('en-IN')} | LCO: ${lco?.code||''}</p>
              <div class="summary">
                <div class="scard"><div class="val">${filtered2.length}</div><div class="lbl">Records</div></div>
                <div class="scard"><div class="val">₹${totAmt2.toLocaleString('en-IN')}</div><div class="lbl">Total Amount</div></div>
                <div class="scard"><div class="val" style="color:#2563eb">₹${totPaid2.toLocaleString('en-IN')}</div><div class="lbl">Total Collected</div></div>
                <div class="scard"><div class="val" style="color:#dc2626">₹${totBal2.toLocaleString('en-IN')}</div><div class="lbl">Balance Due</div></div>
              </div>
              <table><thead><tr><th>Customer</th><th>Username</th><th>Plan</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Type</th><th>Status</th><th>Date</th></tr></thead><tbody>${rows}${totalRow}</tbody></table></body></html>`);
              w.document.close();w.print();
            }} style={{padding:'6px 14px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',background:'#dc2626',color:'white'}}>
              🖨️ PDF
            </button>
          </div>

          {/* Search */}
          <div style={{marginBottom:'12px'}}>
            <input type="text" value={paySearch} onChange={e=>setPaySearch(e.target.value)}
              placeholder="🔍 Search by name or username..."
              style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
          </div>

          {payLoading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : (
            <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                <thead><tr style={{background:'#f8fafc'}}>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Customer</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Plan</th>
                  <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Amount</th>
                  <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Paid</th>
                  <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Balance</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Type</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Status</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Date</th>
                  <th style={{padding:'10px 12px',textAlign:'center',fontWeight:'700',color:'#64748b'}}>Action</th>
                </tr></thead>
                <tbody>
                  {(payments.filter(p=>!paySearch||p.customer_name?.toLowerCase().includes(paySearch.toLowerCase())||p.username?.toLowerCase().includes(paySearch.toLowerCase()))).length===0 ? (
                    <tr><td colSpan={9} style={{padding:'30px',textAlign:'center',color:'#94a3b8'}}>No records found</td></tr>
                  ) : payments.filter(p=>!paySearch||p.customer_name?.toLowerCase().includes(paySearch.toLowerCase())||p.username?.toLowerCase().includes(paySearch.toLowerCase())).map((p,i)=>(
                    <tr key={i} onClick={()=>openHistory(p)} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white',cursor:'pointer'}} title='Click to view payment history'>
                      <td style={{padding:'8px 12px'}}>
                        <div style={{fontWeight:'700',fontSize:'12px'}}>{p.customer_name}</div>
                        <div style={{fontSize:'10px',color:'#94a3b8'}}>{p.username}</div>
                      </td>
                      <td style={{padding:'8px 12px',color:'#64748b'}}>{p.plan}</td>
                      <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700'}}>₹{p.plan_amount?.toLocaleString('en-IN')}</td>
                      <td style={{padding:'8px 12px',textAlign:'right',color:'#10b981',fontWeight:'700'}}>₹{(p.total_paid||0).toLocaleString('en-IN')}</td>
                      <td style={{padding:'8px 12px',textAlign:'right',color:p.balance>0?'#ef4444':'#10b981',fontWeight:'700'}}>₹{(p.balance||0).toLocaleString('en-IN')}</td>
                      <td style={{padding:'8px 12px'}}>
                        <span style={{padding:'2px 8px',borderRadius:'12px',fontSize:'10px',fontWeight:'700',
                          background:p.payment_type==='Online'?'#eff6ff':'#fef9c3',
                          color:p.payment_type==='Online'?'#2563eb':'#854d0e'}}>
                          {p.payment_type}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px'}}>
                        <span style={{padding:'2px 8px',borderRadius:'12px',fontSize:'10px',fontWeight:'700',
                          background:p.collection_status==='collected'?'#f0fdf4':p.collection_status==='pending'?'#fef2f2':p.collection_status==='partial'?'#fffbeb':'#eff6ff',
                          color:p.collection_status==='collected'?'#16a34a':p.collection_status==='pending'?'#dc2626':p.collection_status==='partial'?'#d97706':'#2563eb'}}>
                          {p.collection_status}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',color:'#64748b',fontSize:'11px'}}>
                        {p.transaction_date ? new Date(p.transaction_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td style={{padding:'8px 12px',textAlign:'center'}}>
                        {(p.collection_status==='pending'||p.collection_status==='partial') && (
                          <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                            <button onClick={()=>{setCollectModal(p);setCollectAmt(p.balance?.toFixed(2));setCollectDate(new Date().toISOString().split('T')[0]);}}
                              style={{padding:'4px 10px',background:'#10b981',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>
                              💰 Collect
                            </button>
                            <button onClick={async()=>{
                                if(!paySetup?.is_active){ alert('⚙️ Please complete Payment Setup first!\n\nGo to: 💳 Payment Setup in the sidebar\nEnter your UPI ID to start accepting payments.'); return; }
                                const defaultAmt = (p.balance||p.plan_amount||0).toFixed(2);
                                const enteredAmt = window.prompt('Enter payment amount (₹):',defaultAmt);
                                if(!enteredAmt) return;
                                const amt = parseFloat(enteredAmt);
                                if(isNaN(amt)||amt<=0){ alert('Invalid amount'); return; }
                                setSendingLink(s=>({...s,[p.username]:true}));
                                try {
                                  const r = await lcoPaymentAPI.createLink({
                                    customer_id: p.username,
                                    amount: amt,
                                    plan_name: p.plan
                                  });
                                  if(r.data?.payment_link) {
                                    navigator.clipboard.writeText(r.data.payment_link).catch(()=>{});
                                    alert('✅ Payment link created!\n\n'+r.data.payment_link+'\n\nLink copied to clipboard. Share with customer via WhatsApp.');
                                  }
                                } catch(e){ alert('Failed: '+(e.response?.data?.message||e.message)); }
                                setSendingLink(s=>({...s,[p.username]:false}));
                              }} disabled={sendingLink[p.username]}
                              style={{padding:'4px 10px',background:paySetup?.is_active?'#7c3aed':'#94a3b8',color:'white',border:'none',
                                borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'700',
                                opacity:sendingLink[p.username]?0.6:1}}>
                              {sendingLink[p.username]?'..':'🔗 Pay Link'}
                            </button>
                          </div>
                        )}
                        {p.payment_entries?.length>0 && (
                          <div style={{marginTop:'4px'}}>
                            {p.payment_entries.map((entry,ei)=>(
                              <div key={ei} style={{fontSize:'10px',color:'#64748b',display:'flex',gap:'4px',alignItems:'center',marginTop:'2px'}}>
                                <span>₹{entry.amount} {entry.mode} {entry.date?new Date(entry.date).toLocaleDateString('en-IN'):''}</span>
                                <button onClick={()=>{
                                  setEditEntryModal({collection:p, entryIndex:ei});
                                  setEditEntryAmt(entry.amount?.toString());
                                  setEditEntryMode(entry.mode||'Cash');
                                  setEditEntryNote(entry.note||'');
                                  setEditEntryDate(entry.date?new Date(entry.date).toISOString().split('T')[0]:'');
                                }} style={{padding:'1px 5px',background:'#f1f5f9',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'9px'}}>✏️</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* History Drawer */}
          {historyDrawer && (
            <div style={{position:'fixed',inset:0,zIndex:1000}} onClick={()=>setHistoryDrawer(null)}>
              <div onClick={e=>e.stopPropagation()}
                style={{position:'fixed',top:0,right:0,width:'380px',maxWidth:'95vw',height:'100vh',background:'white',
                  boxShadow:'-4px 0 24px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column',zIndex:1001}}>
                
                {/* Drawer Header */}
                <div style={{padding:'16px',borderBottom:'1px solid #f1f5f9',background:'#f8fafc'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
                    <div style={{fontWeight:'800',fontSize:'15px',color:'#0f172a'}}>👤 {historyDrawer.name}</div>
                    <button onClick={()=>setHistoryDrawer(null)}
                      style={{border:'none',background:'none',cursor:'pointer',fontSize:'18px',color:'#94a3b8'}}>✕</button>
                  </div>
                  <div style={{fontSize:'12px',color:'#64748b'}}>
                    Username: <strong>{historyDrawer.username}</strong>
                  </div>
                </div>

                {/* Year tabs */}
                {historyData?.years?.length>0 && (
                  <div style={{padding:'8px 16px',borderBottom:'1px solid #f1f5f9',display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    {historyData.years.map(y=>(
                      <button key={y} onClick={()=>{setHistoryYear(y.toString());loadHistory(historyDrawer.username,y.toString());}}
                        style={{padding:'4px 12px',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                          background:historyYear===y.toString()?primary:'#f1f5f9',
                          color:historyYear===y.toString()?'white':'#64748b'}}>
                        {y}
                      </button>
                    ))}
                  </div>
                )}

                {/* Summary bar */}
                {historyData?.summary && (
                  <div style={{padding:'8px 16px',background:'#eff6ff',borderBottom:'1px solid #bfdbfe',display:'flex',gap:'12px',flexWrap:'wrap'}}>
                    <span style={{fontSize:'11px',color:'#1e40af'}}><strong>{historyData.summary.total}</strong> renewals</span>
                    <span style={{fontSize:'11px',color:'#16a34a'}}>Paid: <strong>₹{(historyData.summary.total_paid||0).toLocaleString('en-IN')}</strong></span>
                    <span style={{fontSize:'11px',color:'#dc2626'}}>Pending: <strong>₹{(historyData.summary.total_balance||0).toLocaleString('en-IN')}</strong></span>
                  </div>
                )}

                {/* Records list */}
                <div style={{flex:1,overflowY:'auto',padding:'8px 0'}}>
                  {historyLoading ? (
                    <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div>
                  ) : historyData?.records?.length===0 ? (
                    <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>No records for {historyYear}</div>
                  ) : (()=>{
                    // Group by month
                    const grouped = {};
                    (historyData?.records||[]).forEach(r=>{
                      const d = new Date(r.transaction_date);
                      const key = d.toLocaleString('en-IN',{month:'long',year:'numeric'});
                      if(!grouped[key]) grouped[key]=[];
                      grouped[key].push(r);
                    });
                    return Object.entries(grouped).map(([month, recs])=>(
                      <div key={month}>
                        <div style={{padding:'6px 16px',background:'#f1f5f9',fontSize:'11px',fontWeight:'700',color:'#64748b',
                          position:'sticky',top:0}}>
                          ── {month} ──
                        </div>
                        {recs.map((r,ri)=>(
                          <div key={ri} style={{padding:'10px 16px',borderBottom:'1px solid #f8fafc'}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                              <div>
                                <div style={{fontSize:'12px',fontWeight:'700',color:'#0f172a'}}>
                                  {new Date(r.transaction_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                                  <span style={{marginLeft:'8px',fontSize:'11px',fontWeight:'400',color:'#64748b'}}>{r.plan}</span>
                                </div>
                                <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>
                                  {r.payment_type==='Online'?'🌐 Online':'💵 Cash'}
                                  {r.payment_entries?.map((e,ei)=>(
                                    <span key={ei} style={{marginLeft:'6px',color:'#16a34a'}}>
                                      ✅ ₹{e.amount} {e.mode} {e.date?new Date(e.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}):''}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div style={{textAlign:'right'}}>
                                <div style={{fontWeight:'800',fontSize:'13px',color:'#0f172a'}}>₹{r.plan_amount?.toLocaleString('en-IN')}</div>
                                <span style={{padding:'2px 6px',borderRadius:'10px',fontSize:'10px',fontWeight:'700',
                                  background:r.collection_status==='collected'||r.collection_status==='online'?'#f0fdf4':r.collection_status==='partial'?'#fffbeb':'#fef2f2',
                                  color:r.collection_status==='collected'||r.collection_status==='online'?'#16a34a':r.collection_status==='partial'?'#d97706':'#dc2626'}}>
                                  {r.collection_status==='online'?'Online Paid':r.collection_status}
                                  {r.balance>0?` (₹${r.balance} due)`:''}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Edit Entry Modal */}
          {editEntryModal && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001}}>
              <div style={{background:'white',borderRadius:'16px',padding:'24px',width:'320px',maxWidth:'90vw'}}>
                <h3 style={{margin:'0 0 4px',fontSize:'16px',fontWeight:'800'}}>✏️ Edit Payment Entry</h3>
                <p style={{margin:'0 0 16px',fontSize:'12px',color:'#64748b'}}>{editEntryModal.collection.customer_name}</p>
                <div style={{display:'grid',gap:'12px'}}>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Payment Date</label>
                    <input type="date" value={editEntryDate} onChange={e=>setEditEntryDate(e.target.value)}
                      min="2024-01-01" max={new Date().toISOString().split('T')[0]}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Amount</label>
                    <input type="number" value={editEntryAmt} onChange={e=>setEditEntryAmt(e.target.value)}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Mode</label>
                    <select value={editEntryMode} onChange={e=>setEditEntryMode(e.target.value)}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                      <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Cheque</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Note</label>
                    <input type="text" value={editEntryNote} onChange={e=>setEditEntryNote(e.target.value)}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{display:'flex',gap:'8px'}}>
                    <button onClick={()=>setEditEntryModal(null)}
                      style={{flex:1,padding:'10px',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',background:'white'}}>
                      Cancel
                    </button>
                    <button onClick={submitEditEntry}
                      style={{flex:1,padding:'10px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',background:'#2563eb',color:'white'}}>
                      💾 Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Collection Modal */}
          {collectModal && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
              <div style={{background:'white',borderRadius:'16px',padding:'24px',width:'340px',maxWidth:'90vw'}}>
                <h3 style={{margin:'0 0 4px',fontSize:'16px',fontWeight:'800'}}>💰 Record Payment</h3>
                <p style={{margin:'0 0 16px',fontSize:'12px',color:'#64748b'}}>{collectModal.customer_name} — {collectModal.plan}</p>
                <div style={{display:'grid',gap:'12px'}}>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Payment Date</label>
                    <input type="date" value={collectDate} onChange={e=>setCollectDate(e.target.value)}
                      min="2024-01-01" max={new Date().toISOString().split('T')[0]}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Amount (Balance: ₹{collectModal.balance?.toFixed(2)})</label>
                    <input type="number" value={collectAmt} onChange={e=>setCollectAmt(e.target.value)}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Payment Mode</label>
                    <select value={collectMode} onChange={e=>setCollectMode(e.target.value)}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                      <option>Cash</option>
                      <option>UPI</option>
                      <option>Bank Transfer</option>
                      <option>Cheque</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Note (optional)</label>
                    <input type="text" value={collectNote} onChange={e=>setCollectNote(e.target.value)}
                      placeholder="Any note..."
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{display:'flex',gap:'8px',marginTop:'4px'}}>
                    <button onClick={()=>setCollectModal(null)}
                      style={{flex:1,padding:'10px',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',background:'white'}}>
                      Cancel
                    </button>
                    <button onClick={submitCollection}
                      style={{flex:1,padding:'10px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',background:'#10b981',color:'white'}}>
                      ✅ Save Payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab==='staff' && user?.role==='lco' && (
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
            <h3 style={{margin:0,fontSize:'16px',fontWeight:'800'}}>👥 My Staff</h3>
            <button onClick={()=>{setEditStaff(null);setStaffForm({name:'',email:'',phone:'',role:'lco_tech',password:''});setShowStaffForm(true);}}
              style={{padding:'8px 16px',background:primary,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
              ➕ Add Staff
            </button>
          </div>

          {staffLoading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : (
            <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                <thead><tr style={{background:'#f8fafc'}}>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Name</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Mobile</th>
                  <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Role</th>
                  <th style={{padding:'10px 12px',textAlign:'center',fontWeight:'700',color:'#64748b'}}>Actions</th>
                </tr></thead>
                <tbody>
                  {staff.length===0 ? (
                    <tr><td colSpan={5} style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>No staff added yet</td></tr>
                  ) : staff.map((s,i)=>(
                    <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white'}}>
                      <td style={{padding:'8px 12px',fontWeight:'700'}}>{s.name}</td>
                      <td style={{padding:'8px 12px',color:'#64748b'}}>{s.phone||'-'}</td>
                      <td style={{padding:'8px 12px'}}>
                        <span style={{padding:'3px 10px',borderRadius:'12px',fontSize:'11px',fontWeight:'700',
                          background:s.role==='lco_tech'?'#eff6ff':'#f0fdf4',
                          color:s.role==='lco_tech'?'#2563eb':'#16a34a'}}>
                          {s.role==='lco_tech'?'🔧 Technical':'💼 Accountant'}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',textAlign:'center',display:'flex',gap:'6px',justifyContent:'center'}}>
                        <button onClick={()=>{setEditStaff(s);setStaffForm({name:s.name,mobile:s.phone||'',role:s.role,password:''});setShowStaffForm(true);}}
                          style={{padding:'4px 10px',background:'#f1f5f9',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>✏️ Edit</button>
                        <button onClick={()=>deleteStaff(s._id)}
                          style={{padding:'4px 10px',background:'#fef2f2',color:'#dc2626',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>🗑️ Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Staff Form Modal */}
          {showStaffForm && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
              <div style={{background:'white',borderRadius:'16px',padding:'24px',width:'360px',maxWidth:'90vw'}}>
                <h3 style={{margin:'0 0 16px',fontSize:'16px',fontWeight:'800'}}>{editStaff?'✏️ Edit Staff':'➕ Add Staff'}</h3>
                <div style={{display:'grid',gap:'12px'}}>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Full Name *</label>
                    <input value={staffForm.name} onChange={e=>setStaffForm(f=>({...f,name:e.target.value}))}
                      placeholder="Staff name"
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Mobile Number * (used for login)</label>
                    <input value={staffForm.mobile} onChange={e=>setStaffForm(f=>({...f,mobile:e.target.value}))}
                      placeholder="10-digit mobile number" type="tel" maxLength={10}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Role *</label>
                    <select value={staffForm.role} onChange={e=>setStaffForm(f=>({...f,role:e.target.value}))}
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                      <option value="lco_tech">🔧 Technical Staff</option>
                      <option value="lco_accounts">💼 Accountant</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>{editStaff?'New Password (leave blank to keep)':'Password *'}</label>
                    <input value={staffForm.password} onChange={e=>setStaffForm(f=>({...f,password:e.target.value}))}
                      placeholder={editStaff?'Leave blank to keep current':'Set password'} type="password"
                      style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{display:'flex',gap:'8px',marginTop:'4px'}}>
                    <button onClick={()=>{setShowStaffForm(false);setEditStaff(null);}}
                      style={{flex:1,padding:'10px',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',background:'white'}}>
                      Cancel
                    </button>
                    <button onClick={submitStaff}
                      style={{flex:1,padding:'10px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',background:primary,color:'white'}}>
                      {editStaff?'💾 Update':'✅ Create'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab==='network' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'16px',marginBottom:'20px'}}>
            {olts.map(olt=>(
              <div key={olt._id} onClick={()=>selectedOlt?._id===olt._id?setSelectedOlt(null):loadOltOnts(olt)}
                style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid '+(olt.status==='online'?'#bbf7d0':'#fecaca'),cursor:'pointer',transition:'all 0.2s',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}
                onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
                onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
                  <div>
                    <div style={{fontWeight:'700',fontSize:'15px',color:'#0f172a'}}>{olt.switch_name||olt.switch_ip}</div>
                    <div style={{fontSize:'12px',color:'#64748b',fontFamily:'monospace'}}>{olt.switch_ip}</div>
                  </div>
                  <span style={{padding:'4px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',background:olt.status==='online'?'#ecfdf5':'#fef2f2',color:olt.status==='online'?'#10b981':'#ef4444'}}>{olt.status}</span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',fontSize:'13px'}}>
                  <div style={{textAlign:'center',padding:'8px',background:'#f8fafc',borderRadius:'8px'}}>
                    <div style={{fontWeight:'800',fontSize:'18px',color:'#0f172a'}}>{olt.total_onts||0}</div>
                    <div style={{color:'#64748b',fontSize:'11px'}}>Total</div>
                  </div>
                  <div style={{textAlign:'center',padding:'8px',background:'#ecfdf5',borderRadius:'8px'}}>
                    <div style={{fontWeight:'800',fontSize:'18px',color:'#10b981'}}>{olt.onts_online||0}</div>
                    <div style={{color:'#10b981',fontSize:'11px'}}>Online</div>
                  </div>
                  <div style={{textAlign:'center',padding:'8px',background:'#fef2f2',borderRadius:'8px'}}>
                    <div style={{fontWeight:'800',fontSize:'18px',color:'#ef4444'}}>{olt.onts_offline||0}</div>
                    <div style={{color:'#ef4444',fontSize:'11px'}}>Offline</div>
                  </div>
                </div>
                <div style={{marginTop:'10px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:'12px',fontWeight:'700',padding:'3px 8px',background:'#f1f5f9',borderRadius:'6px',color:'#475569'}}>{olt.olt_vendor||'Unknown'}</span>
                  <span style={{fontSize:'11px',color:'#94a3b8'}}>Updated: {olt.last_updated?new Date(olt.last_updated).toLocaleTimeString('en-IN'):'-'}</span>
                </div>
                <div style={{marginTop:'10px',display:'flex',gap:'6px'}}>
                  <button onClick={e=>runOltAI(olt,e)} style={{flex:1,padding:'6px',background:'#7c3aed',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>🔬 PON Analysis</button>
                  <button onClick={e=>runCongestion(olt,e)} style={{flex:1,padding:'6px',background:'#dc2626',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>🔥 Congestion</button>
                </div>
              </div>
            ))}
          </div>

          {/* ONT List Modal */}
          {selectedOlt && (
            <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
              <div style={{padding:'16px 20px',background:'#f8fafc',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <span style={{fontWeight:'700',fontSize:'15px'}}>{selectedOlt.switch_name||selectedOlt.switch_ip}</span>
                  <span style={{marginLeft:'8px',fontSize:'12px',color:'#64748b'}}>{oltOnts.length} ONTs</span>
                </div>
                <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                  <input value={ontSearch} onChange={e=>setOntSearch(e.target.value)}
                    placeholder="Search serial, name..." style={{padding:'6px 12px',border:'1px solid #e2e8f0',borderRadius:'6px',fontSize:'12px',width:'200px'}} />
                  <button onClick={()=>{setSelectedOlt(null);setOltOnts([]);}} style={{border:'none',background:'none',cursor:'pointer',fontSize:'18px',color:'#94a3b8'}}>✕</button>
                </div>
              </div>
              {oltLoading ? (
                <div style={{padding:'32px',textAlign:'center',color:'#94a3b8'}}>Loading ONTs...</div>
              ) : (
                <div style={{maxHeight:'500px',overflowY:'auto'}}>
                  {(() => {
                    const filtered = oltOnts.filter(o=>!ontSearch||(o.serial||'').toLowerCase().includes(ontSearch.toLowerCase())||(o.name||'').toLowerCase().includes(ontSearch.toLowerCase()));
                    // Group by PON
                    const ponGroups = {};
                    filtered.forEach(o=>{
                      const ponKey = 'PON'+(o.pon||'?');
                      if(!ponGroups[ponKey]) ponGroups[ponKey]={onts:[],online:0,offline:0};
                      ponGroups[ponKey].onts.push(o);
                      if(o.status==='online') ponGroups[ponKey].online++;
                      else ponGroups[ponKey].offline++;
                    });
                    return Object.entries(ponGroups).sort(([a],[b])=>a.localeCompare(b,undefined,{numeric:true})).map(([pon,group])=>{
                      const expanded = expandedPons[pon]===true; // default collapsed
                      const hasIssue = group.offline > 0;
                      return (
                        <div key={pon} style={{borderBottom:'1px solid #f1f5f9'}}>
                          {/* PON Header - clickable */}
                          <div onClick={()=>setExpandedPons(p=>({...p,[pon]:!expanded}))}
                            style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#f8fafc',cursor:'pointer',userSelect:'none'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                            onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}>
                            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                              <span style={{fontSize:'16px'}}>{expanded?'▼':'▶'}</span>
                              <span style={{fontWeight:'700',fontSize:'13px'}}>{pon}</span>
                              <span style={{fontSize:'12px',color:'#64748b'}}>{group.onts.length} ONTs</span>
                              <span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',background:'#ecfdf5',color:'#10b981'}}>{group.online} online</span>
                              {group.offline>0 && <span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',background:'#fef2f2',color:'#ef4444'}}>{group.offline} offline</span>}
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <span style={{fontSize:'12px',color:'#94a3b8'}}>{Math.round(group.online/group.onts.length*100)}% up</span>
                              {group.online>=100 && <span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',background:'#fee2e2',color:'#dc2626'}}>🔴 Critical PON Load</span>}
                              {group.online>=80 && group.online<100 && <span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',background:'#fef3c7',color:'#d97706'}}>🟡 Marginal PON Load</span>}
                              {group.online<80 && <span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',background:'#ecfdf5',color:'#10b981'}}>✅ Normal</span>}
                            </div>
                          </div>
                          {/* ONT List - collapsible */}
                          {expanded && (
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                              <thead><tr style={{background:'#fafafa'}}>
                                {['ONU','Serial','Vendor/Model','Distance','RX Power','Status'].map(h=>(
                                  <th key={h} style={{padding:'6px 12px',textAlign:'left',fontWeight:'600',color:'#94a3b8',fontSize:'11px'}}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {group.onts.map((o,i)=>{
                                  const online = o.status==='online';
                                  const rx = typeof o.rx_power==='number'?o.rx_power:parseFloat(o.rx_power);
                                  const rxColor = !isNaN(rx)?(rx>=-24?'#10b981':rx>=-27?'#f59e0b':'#ef4444'):'#94a3b8';
                                  return (
                                    <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#f8fafc':'white'}}>
                                      <td style={{padding:'7px 12px',fontWeight:'600',paddingLeft:'28px'}}>{o.name}</td>
                                      <td style={{padding:'7px 12px',fontFamily:'monospace',fontSize:'11px'}}>{o.serial}</td>
                                      <td style={{padding:'7px 12px',color:'#64748b'}}>{o.vendor} {o.model}</td>
                                      <td style={{padding:'7px 12px',color:'#64748b',fontSize:'11px'}}>{o.distance_m?Math.round(o.distance_m/100)/10+' km':'-'}</td>
                                      <td style={{padding:'7px 12px',color:rxColor,fontWeight:'600'}}>{o.rx_power!==undefined?o.rx_power+' dBm':'-'}</td>
                                      <td style={{padding:'7px 12px'}}><span style={{padding:'2px 8px',borderRadius:'20px',fontSize:'10px',fontWeight:'700',background:online?'#ecfdf5':'#fef2f2',color:online?'#10b981':'#ef4444'}}>{o.status}</span></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Congestion Modal */}
      {showCongModal && (
        <div onClick={()=>setShowCongModal(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'white',borderRadius:'16px',padding:'28px',width:'560px',maxWidth:'90vw',maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{margin:0,fontSize:'18px',fontWeight:'800'}}>🔥 PON Congestion Analysis — {congTitle}</h3>
              <button onClick={()=>setShowCongModal(false)} style={{border:'none',background:'none',cursor:'pointer',fontSize:'20px',color:'#94a3b8'}}>✕</button>
            </div>
            {congLoading ? (
              <div style={{textAlign:'center',padding:'32px',color:'#94a3b8'}}>
                <div style={{width:'40px',height:'40px',border:'3px solid #dc262622',borderTop:'3px solid #dc2626',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 12px'}}/>
                Analyzing PON traffic...
              </div>
            ) : congData?.noData ? (
              <div style={{textAlign:'center',padding:'32px',color:'#94a3b8'}}>
                <div style={{fontSize:'36px'}}>⏳</div>
                <div style={{fontWeight:'600',margin:'12px 0'}}>Insufficient traffic data</div>
                <div style={{fontSize:'12px'}}>Need at least 2 poll cycles. Check back in 10 minutes.</div>
              </div>
            ) : congData?.error ? (
              <div style={{color:'#ef4444',padding:'16px'}}>{congData.error}</div>
            ) : congData ? (
              <div>
                <div style={{fontSize:'12px',color:'#64748b',marginBottom:'12px'}}>Based on {congData.history} traffic samples (last 1 hour)</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr style={{background:'#f8fafc'}}>
                    {['Port','↓ Down','↑ Up','Max Util','ONTs','Per ONT','Status'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',fontWeight:'600',color:'#64748b'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(congData.portStats||[]).map((p,i)=>(
                      <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#f8fafc':'white'}}>
                        <td style={{padding:'8px 10px',fontWeight:'700'}}>{p.icon} {p.name}</td>
                        <td style={{padding:'8px 10px',color:'#3b82f6'}}>{p.outMbps} Mbps<br/><span style={{fontSize:'10px',color:'#94a3b8'}}>{p.downUtil}%</span></td>
                        <td style={{padding:'8px 10px',color:'#10b981'}}>{p.inMbps} Mbps<br/><span style={{fontSize:'10px',color:'#94a3b8'}}>{p.upUtil}%</span></td>
                        <td style={{padding:'8px 10px',fontWeight:'700',color:p.color}}>{p.maxUtil}%</td>
                        <td style={{padding:'8px 10px'}}>{p.ontsOnline}/{p.ontsTotal}</td>
                        <td style={{padding:'8px 10px',color:'#7c3aed'}}>{p.perOntDown} Mbps</td>
                        <td style={{padding:'8px 10px'}}><span style={{padding:'3px 8px',borderRadius:'20px',fontSize:'10px',fontWeight:'700',background:p.color+'22',color:p.color}}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {showAiModal && (
        <div onClick={()=>setShowAiModal(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'white',borderRadius:'16px',padding:'28px',width:'500px',maxWidth:'90vw',maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{margin:0,fontSize:'18px',fontWeight:'800'}}>🔬 PON Analysis</h3>
              <button onClick={()=>setShowAiModal(false)} style={{border:'none',background:'none',cursor:'pointer',fontSize:'20px',color:'#94a3b8'}}>✕</button>
            </div>
            {aiLoading ? (
              <div style={{textAlign:'center',padding:'32px',color:'#94a3b8'}}>
                <div style={{width:'40px',height:'40px',border:'3px solid #7c3aed22',borderTop:'3px solid #7c3aed',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 12px'}}/>
                Analyzing network data...
              </div>
            ) : aiAnalysis?.error ? (
              <div style={{color:'#ef4444',padding:'16px'}}>{aiAnalysis.error}</div>
            ) : aiAnalysis ? (
              <div>
                {/* Stats */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'16px'}}>
                  {[['Total',aiAnalysis.totalOnts,'#0f172a'],['Online',aiAnalysis.onlineOnts,'#10b981'],['Offline',aiAnalysis.offlineOnts,'#ef4444'],['Health',aiAnalysis.health+'%',aiAnalysis.health>90?'#10b981':aiAnalysis.health>70?'#f59e0b':'#ef4444']].map(([l,v,c])=>(
                    <div key={l} style={{textAlign:'center',padding:'10px',background:'#f8fafc',borderRadius:'8px'}}>
                      <div style={{fontSize:'20px',fontWeight:'800',color:c}}>{v}</div>
                      <div style={{fontSize:'10px',color:'#64748b'}}>{l}</div>
                    </div>
                  ))}
                </div>
                {/* Alerts */}
                {aiAnalysis.alerts.length===0 ? (
                  <div style={{textAlign:'center',padding:'20px',background:'#ecfdf5',borderRadius:'8px'}}>
                    <div style={{fontSize:'28px'}}>✅</div>
                    <div style={{fontWeight:'700',color:'#10b981',margin:'6px 0'}}>OLT Healthy</div>
                    <div style={{fontSize:'12px',color:'#64748b'}}>All PON ports operating normally</div>
                  </div>
                ) : aiAnalysis.alerts.map((a,i)=>(
                  <div key={i} style={{borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:a.sev==='critical'?'#fee2e2':'#fef3c7'}}>
                    <div style={{fontWeight:'700',fontSize:'12px',marginBottom:'4px'}}>{a.msg}</div>
                    <div style={{fontSize:'11px',padding:'6px',background:'rgba(255,255,255,0.8)',borderRadius:'4px',whiteSpace:'pre-wrap'}}>{a.rec}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div onClick={()=>setSelectedCustomer(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'white',borderRadius:'16px',padding:'28px',width:'400px',maxWidth:'90vw'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'20px'}}>
              <h3 style={{margin:0,fontSize:'18px',fontWeight:'800'}}>{selectedCustomer.name}</h3>
              <button onClick={()=>setSelectedCustomer(null)} style={{border:'none',background:'none',cursor:'pointer',fontSize:'20px',color:'#94a3b8'}}>✕</button>
            </div>
            <div style={{display:'grid',gap:'12px',fontSize:'14px'}}>
              {[
                ['Username',selectedCustomer.username],
                ['Mobile',selectedCustomer.mobile],
                ['Status',selectedCustomer.status],
                ['Plan',selectedCustomer.plan],
                ['Address',selectedCustomer.address],
                ['Expiry',selectedCustomer.expiry ? new Date(selectedCustomer.expiry).toLocaleDateString('en-IN') : '-'],
              ].map(([k,v])=>(
                <div key={k} style={{display:'flex',gap:'8px'}}>
                  <span style={{color:'#64748b',minWidth:'80px',fontWeight:'600'}}>{k}:</span>
                  <span style={{color:'#0f172a'}}>{v||'-'}</span>
                </div>
              ))}
              <div style={{marginTop:'4px'}}>
                <span style={{padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'700',background:statusBg[selectedCustomer.status]||'#f8fafc',color:statusColor[selectedCustomer.status]||'#64748b'}}>{selectedCustomer.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LCODashboard;
